import { List, ListItemButton, Tooltip, Box } from "@mui/material";
import {
  GridApiPremium,
  gridRowSelectionIdsSelector,
} from "@mui/x-data-grid-premium";

interface SupplierStoreDrawerProps {
  inventoryGridApiRef: React.MutableRefObject<GridApiPremium | null>;
}

interface StoreConfig {
  name: string;
  displayName: string;
  hasCheckmark?: boolean;
}

function getStoreLinks(store: string, row: any): string {
  const storeProductPage: any = {
    amazon: row?.["supply"]["supply_asin"]
      ? `https://www.amazon.com/gp/offer-listing/${row?.["supply"]["supply_asin"]}/ref=olp_f_freeShipping?ie=UTF8&f_freeShipping=true&f_new=true`
      : `https://www.amazon.com/s/ref=nb_sb_noss?url=search-alias%3Daps&field-keywords=${row?.["supply"]["supply_asin"]}`,
    amazonca: row?.["supply"]["supply_asin"]
      ? `https://www.amazon.ca/gp/offer-listing/${row?.["supply"]["supply_asin"]}/ref=olp_f_freeShipping?ie=UTF8&f_new=true`
      : `https://www.amazon.ca/s/ref=nb_sb_noss?url=search-alias%3Daps&field-keywords=${row?.["supply"]["supply_asin"]}&fap=1`,
    amazonuk: `https://www.amazon.co.uk/s/ref=nb_sb_noss?url=search-alias%3Daps&field-keywords=${row?.["supply"]["supply_asin"]}`,
    alliance: `https://webami.aent.com/${row?.["supply"]["upc"]}`,
    barnesandnoble: `https://www.barnesandnoble.com/s/${row?.["supply"]["upc"]}`,
    bestbuy: `https://www.bestbuy.com/site/searchpage.jsp?st=${row?.["supply"]["upc"]}&_dyncharset=UTF-8&id=pcat17071`,
    blowitoutahere: `https://www.blowitoutahere.com/${row?.["supply"]["upc"]}`,
    bookdepo: `https://www.bookdepository.com/a/${row?.["supply"]["upc"]}`,
    bullmoose: `https://www.bullmoose.com/search?q=${row?.["supply"]["upc"]}`,
    camel: `https://camelcamelcamel.com/search?sq=${row?.["supply"]["upc"]}`,
    ccvideo: `https://www.ccvideo.com/${row?.["supply"]["upc"]}`,
    "collectors choice music": `https://www.ebay.com/sch/i.html?_dkr=1&iconV2Request=true&_blrs=recall_filtering&_ssn=collectorschoicemusic&store_cat=0&store_name=getimportcds&_oac=1&_nkw=${row?.["supply"]["upc"]}`,
    chapters: `https://www.chapters.indigo.ca/en-ca/home/search/?keywords=${row?.["supply"]["upc"]}#facetIds=321011`,
    "couch potato": `https://www.couchpotato.com/store/search/?q=${row?.["supply"]["upc"]}`,
    deepdiscount: `https://www.deepdiscount.com/${row?.["supply"]["upc"]}`,
    dealsareus: `https://b2b.dealsareus.ca/${row?.["supply"]["upc"]}`,
    ebayv1: [
      `https://www.ebay.com/sch/i.html?_from=R40&_nkw=${row?.["supply"]["upc"]}
      &LH_TitleDesc=0&_sac=1&_sop=15&_fsrp=1&LH_All=1&_oac=1&rt=nc&LH_ItemCondition=2`,
      `https://www.ebay.com/sch/i.html?_from=R40&_nkw=${row?.["supply"]["upc"]}&LH_TitleDesc=0&_sac=1&_sop=15&_fsrp=1&LH_All=1&_oac=1&rt=nc&LH_ItemCondition=3`,
    ],
    popmarket: `https://www.ebay.com/sch/i.html?_dkr=1&iconV2Request=true&_ssn=pop.market&store_cat=0&store_name=blowitoutahere&_oac=1&_nkw=${row?.["supply"]["upc"]}`,
    familyvideo: `https://www.familyvideo.com/catalog/product_info.php?products_id=${row?.["supply"]["upc"]}`,
    frys: `https://www.frys.com/search?resultpage=0&start=0&rows=25&pType=pDisplay&cat=-68984&inq=${row?.["supply"]["upc"]}`,
    heartland: `https://www.heartlandmusic.com/${row?.["supply"]["upc"]}`,
    gruv: `https://www.ebay.com/sch/i.html?_dkr=1&iconV2Request=true&_ssn=gruv-entertainment&store_cat=0&store_name=gruventertainment&_oac=1&_nkw=${row?.["supply"]["upc"]}`,
    importcds: `https://www.importcds.com/${row?.["supply"]["upc"]}`,
    importcdsebay: `https://www.ebay.com/sch/i.html?_dkr=1&iconV2Request=true&_ssn=get_importcds&store_cat=0&store_name=getimportcds&_oac=1&_nkw=${row?.["supply"]["upc"]}`,
    mila: `https://milavideo.com/index.php?fc=module&module=czproductsearch&controller=productsearch&cate=&search_query=${row?.["supply"]["upc"]}`,
    rarewaves: `https://www.rarewaves.com/search?q=${row?.["supply"]["upc"]}`,
    rakuten: `https://www.rakuten.com/sr/searchresults#qu=${row?.["supply"]["upc"]}"&rt=1&page=1`,
    rightstuf: `https://www.rightstufanime.com/search?keywords=${row?.["supply"]["upc"]}`,
    target: `https://www.target.com/s?searchTerm=${row?.["supply"]["upc"]}`,
    walmart: `https://www.walmart.com/ip/${row?.["supply"]["walsku"]}`,
    walmartca: `https://walmart.ca/search/${row?.["supply"]["walsku"]}`,
    wowhd: `https://www.wowhd.us/${row?.["supply"]["upc"]}`,
  };

  return storeProductPage[store] || "";
}

const stores: StoreConfig[] = [
  { name: "dealsareus", displayName: "Deals Are Us" },
  { name: "alliance", displayName: "Alliance" },
  { name: "walmart", displayName: "Walmart" },
  { name: "amazon", displayName: "Amazon" },
  { name: "ebayv1", displayName: "Ebay Used and New" },
  { name: "target", displayName: "Target" },
  { name: "popmarket", displayName: "Popmarket" },
  { name: "gruv", displayName: "Gruv" },
  { name: "rarewaves", displayName: "Rarewaves" },
  { name: "importcds", displayName: "ImportCDs" },
  { name: "ccvideo", displayName: "CC Video" },
  { name: "deepdiscount", displayName: "Deep Discount" },
  { name: "wowhd", displayName: "WOW HD" },
  { name: "collectors choice music", displayName: "Collectors Choice Music" },
  { name: "walmartca", displayName: "Walmart CA", hasCheckmark: true },
  { name: "amazonca", displayName: "Amazon CA" },
  { name: "amazonuk", displayName: "Amazon UK" },
  { name: "bestbuy", displayName: "Best Buy" },
  { name: "chapters", displayName: "Chapters" },
  { name: "bullmoose", displayName: "Bull Moose" },
  { name: "mila", displayName: "Mila" },
  { name: "couch potato", displayName: "Couch Potato" },
  { name: "camel", displayName: "Camel" },
  { name: "barnesandnoble", displayName: "Barnes and Noble" },
];

const checkedStores = new Set(["walmartca", "importcdsebay"]);

export function SupplierStoreDrawer({
  inventoryGridApiRef,
}: SupplierStoreDrawerProps) {
  const openLinks = (store: string) => {
    const gridApi = inventoryGridApiRef.current;
    if (!gridApi) return;

    const selectedRowsMap = gridRowSelectionIdsSelector(inventoryGridApiRef);
    const selectedRows = gridApi
      .getSortedRowIds()
      .filter((id) => selectedRowsMap.has(id))
      .reverse()
      .map((id) => selectedRowsMap.get(id));

    if (!selectedRows.length) {
      const url = getStoreLinks(store, undefined);
      if (url) window.open(url, "_blank");
      return;
    }

    selectedRows.forEach((row) => {
      const url = getStoreLinks(store, row);
      if (!Array.isArray(url)) {
        if (url) window.open(url, "_blank");
      } else {
        url.forEach((u) => {
          if (u) window.open(u, "_blank");
        });
      }
    });
  };

  const renderStoreLinks = () => {
    return stores.map((store) => {
      const isDisabled = inventoryGridApiRef.current
        ? gridRowSelectionIdsSelector(inventoryGridApiRef).size === 0
        : true;

      let iconPath = "";
      try {
        iconPath = require(`../../../assets/icons/${store.name}.png`);
      } catch (e) {
        console.warn(`Icon not found for store: ${store.name}`);
      }

      return (
        <Tooltip
          key={store.name}
          title={
            isDisabled
              ? "Please select rows first"
              : `Open ${store.displayName}`
          }
          placement="right"
        >
          <span>
            <ListItemButton
              onClick={() => !isDisabled && openLinks(store.name)}
              disabled={isDisabled}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 1,
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? "not-allowed" : "pointer",
                "&.Mui-disabled": {
                  opacity: 0.5,
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {iconPath && (
                  <img
                    src={iconPath}
                    alt={store.name}
                    height={30}
                    style={{ opacity: isDisabled ? 0.5 : 1 }}
                  />
                )}
                {checkedStores.has(store.name) && (
                  <Box
                    component="i"
                    className="check square outline icon"
                    sx={{
                      ml: 1,
                      opacity: isDisabled ? 0.5 : 1,
                    }}
                  />
                )}
              </Box>
            </ListItemButton>
          </span>
        </Tooltip>
      );
    });
  };

  return <List sx={{ padding: 0 }}>{renderStoreLinks()}</List>;
}
